import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react'
import { Alert, Badge, Button, Card, Col, Container, ListGroup, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Organizations } from '../../api/Organizations';
import AppLoader from '../../components/AppLoader';
import AppSearch from '../../components/AppSearch';
import Avatar from '../../components/Avatar';
import IconButton from '../../components/IconButton';
import { useAuth } from '../../contexts/auth';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import { Organization } from '../../models/Organization';

export default function OrganizationsList() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<Organization[] | null>();
  const [organizationToDelete, setOrganizationToDelete] = useState<Organization>();
  const [searchText, setSearchText] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const { showLoader, hideLoader } = useFullScreenLoader();
  const { showToast } = useToasts();
  const { loggedUser } = useAuth();

  useEffect(() => {
    if (loggedUser) initOrganizations();
    // eslint-disable-next-line
  }, [loggedUser]);

  const createOrganization = () => navigate('/organizations/create');
  const handleDeleteModalClose = () => setShowDeleteModal(false);

  const openDeleteModal = (organization: Organization) => () => {
    setShowDeleteModal(true);
    setOrganizationToDelete(organization);
  }

  const reset = () => {
    setOrganizations(null);
    initOrganizations();
  }

  const handleSearchChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const value = evt.target.value;
    setSearchText(value);
  }

  const deleteOrganization = () => {
    showLoader!();

    Organizations.deleteOrganization(organizationToDelete!.id)
      .then(
        () => {
          hideLoader!();
          showToast!('organization-success', 'Organization deleted successfully', 'Delete Success', 'success');
          handleDeleteModalClose();
          reset();
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('organization-error', message, `Organizations Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const editOrganization = (id: number) => () => navigate(`/organizations/${id}/edit`);

  const initOrganizations = () => {
    Organizations.getOrganizations()
      .then(
        ({ data: organizations }) => setOrganizations(organizations),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          hideLoader!();
          showToast!('organizations-error', message, `Organizations Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }

  const renderOrganizations = () => {
    const filteredOrganizations = organizations!.filter(organization => organization.name.toLowerCase().includes(searchText.toLowerCase()));

    return (
      <>
        <Row className="gx-2 mb-3">
          <Col>
            <AppSearch value={searchText} onChange={handleSearchChange} placeholder={t('organizationsList.search')} />
          </Col>
        </Row>

        {filteredOrganizations.length === 0 && (
          <Alert className="mb-0 py-2">
            <FontAwesomeIcon icon={solid('info-circle')} />
            <span className="ms-2">{t('organizationsList.noGroups')}</span>
          </Alert>
        )}

        <ListGroup>
          {filteredOrganizations.map(organization => (
            <ListGroup.Item key={organization.id} className="d-flex align-items-center">
              <div className="pe-3">
                <Avatar imageUrl={`${window.__env.BASE_URL}/organizations/${organization.id}/logo?access_token=${token}`} />
              </div>

              <Col>
                <h6 className="mb-0">{organization.name}</h6>
                {organization.isInvalid ?
                  <Badge bg="warning" className="border justify-self-end">
                    <span className="">Not ready</span>
                  </Badge> :
                  <Badge bg="success" className="border justify-self-end">
                  <span className="">Active</span>
                </Badge>
                }
              </Col>

              <div className="ps-3 text-end">
                {!organization.isInvalid && <>
                  <IconButton className="ms-2" onClick={editOrganization(organization.id!)}>
                    <FontAwesomeIcon icon={solid('pen')} />
                  </IconButton>
                  <IconButton className="ms-2" onClick={openDeleteModal(organization)}>
                    <FontAwesomeIcon className="text-danger" icon={solid('trash')} />
                  </IconButton>
                </>
                }
              </div>
            </ListGroup.Item>
          ))}
        </ListGroup>
      </>
    )
  }

  const renderAsyncContent = () => {
    return organizations
      ? renderOrganizations()
      : (
        <div className="d-flex justify-content-center py-5">
          <AppLoader size={150} />
        </div>
      )
  }

  return (
    <Container>
      <Card>
        <div className="d-flex align-items-center justify-content-between border-bottom py-2 px-3">
          <Card.Title className="mb-0">{t('organizationsList.title')}</Card.Title>

          <Button variant="success" size="sm" onClick={createOrganization}>
            <FontAwesomeIcon icon={solid('plus')} />
            <span className="ms-2">{t('organizationsList.create')}</span>
          </Button>
        </div>

        <Card.Body>{renderAsyncContent()}</Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={handleDeleteModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common.deleteTitle', { item: t('common.group') })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('organizationsList.deleteMessage')} <b>{organizationToDelete?.name}</b>?

          <h5 className="my-4 text-center text-danger">{t('common.deleteWarning')}</h5>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" className="border" size="sm" onClick={handleDeleteModalClose}>{t('common.cancel')}</Button>
          <Button variant="danger" size="sm" onClick={deleteOrganization}>{t('common.delete')}</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}